import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Lock, Unlock, Calendar as CalIcon, AlertCircle, LayoutGrid, Rows } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const WEEKDAYS_FULL = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const WEEKDAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function fmtMonth(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
function isoDay(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeekMon(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x;
}

export default function AgendaCalendar({ appointments = [], onRefresh }) {
  const today = new Date();
  const [viewMode, setViewMode] = useState("week"); // "week" = 5 jours · "month" = mois
  const [weekStart, setWeekStart] = useState(startOfWeekMon(today));
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() + 1 });
  const [stats, setStats] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockModal, setBlockModal] = useState(null);
  const [blockForm, setBlockForm] = useState({ all_day: true, start_time: "09:00", end_time: "12:00", reason: "" });

  const monthKey = fmtMonth(cursor.y, cursor.m);

  // In week mode, derive cursor from weekStart so backend stats month matches
  useEffect(() => {
    if (viewMode === "week") {
      setCursor({ y: weekStart.getFullYear(), m: weekStart.getMonth() + 1 });
    }
  }, [viewMode, weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, g] = await Promise.all([
        api.get(`/admin/agenda/stats?month=${monthKey}`),
        api.get(`/admin/blocked-slots?month=${monthKey}`),
        api.get(`/admin/google/events?date_from=${monthKey}-01&date_to=${monthKey}-31`).catch(() => ({ data: { connected: false, events: [] } })),
      ]);
      setStats(s.data);
      setBlocked(b.data || []);
      setGoogleEvents((g.data?.events || []).map((e) => ({ ...e, dateOnly: (e.start || "").slice(0, 10) })));
      setGoogleConnected(g.data?.connected || false);
    } catch { toast.error("Erreur de chargement de l'agenda"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthKey]);

  const prev = () => {
    if (viewMode === "week") setWeekStart((w) => addDays(w, -7));
    else setCursor((c) => c.m === 1 ? { y: c.y - 1, m: 12 } : { y: c.y, m: c.m - 1 });
    setSelectedDate(null);
  };
  const next = () => {
    if (viewMode === "week") setWeekStart((w) => addDays(w, 7));
    else setCursor((c) => c.m === 12 ? { y: c.y + 1, m: 1 } : { y: c.y, m: c.m + 1 });
    setSelectedDate(null);
  };
  const todayBtn = () => {
    setWeekStart(startOfWeekMon(new Date()));
    const t = new Date(); setCursor({ y: t.getFullYear(), m: t.getMonth() + 1 });
    setSelectedDate(null);
  };

  const apptCountsByDate = useMemo(() => {
    const map = {};
    appointments.forEach((a) => {
      if (a.preferred_date && a.status !== "annule") map[a.preferred_date] = (map[a.preferred_date] || 0) + 1;
    });
    return map;
  }, [appointments]);

  const blockedByDate = useMemo(() => {
    const map = {}; blocked.forEach((b) => { (map[b.date] = map[b.date] || []).push(b); }); return map;
  }, [blocked]);

  // Month grid
  const monthGrid = useMemo(() => {
    if (!stats || viewMode !== "month") return [];
    const first = new Date(cursor.y, cursor.m - 1, 1);
    const offset = (first.getDay() + 6) % 7;
    const cells = []; for (let i = 0; i < offset; i++) cells.push(null);
    stats.days.forEach((d) => cells.push(d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [stats, cursor, viewMode]);

  // Week (5 days) — Mon to Fri
  const weekDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 5; i++) {
      const d = addDays(weekStart, i);
      const date = isoDay(d);
      const dayStat = (stats?.days || []).find((x) => x.date === date) || { date, rate_pct: 0, capacity: 0 };
      arr.push({ ...dayStat, jsDate: d });
    }
    return arr;
  }, [weekStart, stats]);

  const apptsForSelected = useMemo(() => {
    if (!selectedDate) return [];
    return appointments.filter((a) => a.preferred_date === selectedDate && a.status !== "annule");
  }, [selectedDate, appointments]);

  const openBlockModal = (date) => {
    setBlockModal({ date: date || isoDay(weekStart) });
    setBlockForm({ all_day: true, start_time: "09:00", end_time: "12:00", reason: "" });
  };
  const submitBlock = async () => {
    try {
      await api.post("/admin/blocked-slots", {
        date: blockModal.date,
        date_to: blockModal.date_to || null,
        all_day: blockForm.all_day,
        start_time: blockForm.all_day ? null : blockForm.start_time,
        end_time: blockForm.all_day ? null : blockForm.end_time,
        reason: blockForm.reason,
      });
      toast.success(blockModal.date_to ? "Plage de dates bloquée" : "Créneau bloqué");
      setBlockModal(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const unblock = async (bid) => {
    if (!window.confirm("Lever ce blocage ?")) return;
    try { await api.delete(`/admin/blocked-slots/${bid}`); toast.success("Blocage levé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const todayIso = isoDay(new Date());

  return (
    <div className="bg-white rounded-3xl border border-black/8 p-4 sm:p-5 mb-4" data-testid="agenda-calendar">
      {/* Header bar — controls + block button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalIcon size={18} className="text-[#06B6D4]" />
          <h2 className="display text-xl font-extrabold text-[#0A1128]">Agenda</h2>
          {googleConnected && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#A7F3D0]/40 text-[#065F46] border border-[#A7F3D0]" data-testid="agenda-google-badge" title={`${googleEvents.length} événement(s) Google chargé(s)`}>
              <CalIcon size={10} />Google · {googleEvents.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Block button at top */}
          <Button
            size="sm"
            onClick={() => openBlockModal(selectedDate || todayIso)}
            className="bg-[#0A1128] text-white hover:bg-[#14213d]"
            data-testid="agenda-block-top"
          >
            <Lock size={12} className="mr-1.5" />Bloquer un créneau
          </Button>
          {/* View toggle */}
          <div className="inline-flex rounded-lg border border-black/10 overflow-hidden">
            <button
              onClick={() => setViewMode("week")}
              data-testid="agenda-view-week"
              className={`px-2.5 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${viewMode === "week" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-white text-[#64748B] hover:bg-black/5"}`}
            ><Rows size={12} />5 jours</button>
            <button
              onClick={() => setViewMode("month")}
              data-testid="agenda-view-month"
              className={`px-2.5 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${viewMode === "month" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-white text-[#64748B] hover:bg-black/5"}`}
            ><LayoutGrid size={12} />Mois</button>
          </div>
          {/* Nav */}
          <button onClick={prev} data-testid="agenda-prev" className="p-1.5 rounded-lg hover:bg-black/5"><ChevronLeft size={16} /></button>
          <button onClick={todayBtn} data-testid="agenda-today" className="px-2.5 py-1 text-xs font-bold rounded-lg border border-black/10 hover:bg-black/5">Aujourd'hui</button>
          <button onClick={next} data-testid="agenda-next" className="p-1.5 rounded-lg hover:bg-black/5"><ChevronRight size={16} /></button>
          <div className="display font-bold text-[#0A1128] min-w-[140px] text-center text-sm">
            {viewMode === "week"
              ? `Sem. du ${weekStart.getDate()} ${MONTHS_FR[weekStart.getMonth()].slice(0,3)}.`
              : `${MONTHS_FR[cursor.m - 1]} ${cursor.y}`}
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Taux mois" value={`${stats.month_rate_pct}%`} sub={`${stats.month_booked}/${stats.month_capacity}`} color="#06B6D4" />
          <Stat label="Taux semaine" value={`${stats.week_rate_pct}%`} sub={`${stats.week_booked}/${stats.week_capacity}`} color="#A7F3D0" />
          <Stat label="RDV à venir" value={appointments.filter((a) => a.preferred_date >= todayIso && a.status !== "annule").length} sub="tous statuts" color="#FFDCA8" />
          <Stat label="Blocages" value={blocked.length} sub="ce mois" color="#67E8F9" />
        </div>
      )}

      {/* WEEK VIEW (5 columns Mon-Fri) — Google Calendar-style */}
      {viewMode === "week" && (
        <div className="mt-4 grid grid-cols-5 gap-2" data-testid="agenda-week-grid">
          {weekDays.map((d) => {
            const isToday = d.date === todayIso;
            const isSelected = selectedDate === d.date;
            const dayAppts = appointments.filter((a) => a.preferred_date === d.date && a.status !== "annule").sort((a,b) => (a.preferred_time||"").localeCompare(b.preferred_time||""));
            const dayBlocked = blockedByDate[d.date] || [];
            const dayGoogle = googleEvents.filter((e) => e.dateOnly === d.date && !e.source_besson_appt_id);
            const dayLabel = WEEKDAYS_FULL[(d.jsDate.getDay() + 6) % 7];
            // Occupancy: each appt counts duration (60 min default), each google event counts hours
            const totalCapacityH = 8; // 8h business day
            let bookedMin = dayAppts.reduce((acc, a) => acc + (a.type === "phone_15m" ? 15 : 60), 0);
            dayGoogle.forEach((e) => {
              if (e.start && e.end && e.start.includes("T")) {
                const m = (new Date(e.end) - new Date(e.start)) / 60000;
                if (!isNaN(m) && m > 0) bookedMin += m;
              }
            });
            if (dayBlocked.some((b) => b.all_day)) bookedMin = totalCapacityH * 60;
            const occPct = Math.min(100, Math.round((bookedMin / (totalCapacityH * 60)) * 100));
            const occColor = occPct >= 80 ? "bg-red-500" : occPct >= 50 ? "bg-[#FFDCA8]" : occPct > 0 ? "bg-[#A7F3D0]" : "bg-black/5";
            return (
              <div
                key={d.date}
                onClick={() => setSelectedDate(d.date)}
                className={`rounded-xl border min-h-[300px] p-2.5 cursor-pointer transition-all ${isSelected ? "border-[#06B6D4] ring-2 ring-[#06B6D4]/30" : "border-black/8 hover:border-[#06B6D4]/40"}`}
                data-testid={`agenda-week-day-${d.date}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div>
                    <div className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">{dayLabel.slice(0,3)}</div>
                    <div className={`display font-extrabold text-xl ${isToday ? "text-[#06B6D4]" : "text-[#0A1128]"}`}>{d.jsDate.getDate()}</div>
                  </div>
                  {dayBlocked.length > 0 && <Lock size={12} className="text-red-500" />}
                </div>
                {/* Occupancy bar */}
                <div className="mb-2" title={`${occPct}% rempli (${Math.round(bookedMin/60*10)/10}h / ${totalCapacityH}h)`}>
                  <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
                    <div className={`h-full ${occColor} transition-all`} style={{ width: `${occPct}%` }} />
                  </div>
                  <div className="text-[9px] font-bold text-[#64748B] mt-0.5 tabular-nums">{occPct}% rempli · {dayAppts.length + dayGoogle.length} évén.</div>
                </div>
                {dayBlocked.length > 0 && (
                  <div className="mb-1.5 space-y-1">
                    {dayBlocked.map((b) => (
                      <button key={b.id} onClick={(e) => { e.stopPropagation(); unblock(b.id); }} className="block w-full text-left bg-red-50 text-red-700 text-[10px] px-2 py-1 rounded border border-red-100 hover:bg-red-100" data-testid={`agenda-week-block-${b.id}`}>
                        <Unlock size={9} className="inline mr-1" />{b.all_day ? "Bloqué j." : `${b.start_time}–${b.end_time}`}
                      </button>
                    ))}
                  </div>
                )}
                {dayAppts.length === 0 && dayBlocked.length === 0 && dayGoogle.length === 0 ? (
                  <div className="text-[11px] text-[#94a3b8] italic">Libre</div>
                ) : (
                  <div className="space-y-1">
                    {/* Platform appointments */}
                    {dayAppts.map((a) => (
                      <div key={a.id} className="bg-[#06B6D4]/8 border-l-2 border-[#06B6D4] px-2 py-1 rounded text-[11px]" data-testid={`agenda-week-appt-${a.id}`}>
                        <div className="font-bold tabular-nums text-[#0A1128]">{a.preferred_time || "—"}</div>
                        <div className="text-[#0A1128] truncate font-medium">{a.name}</div>
                        <div className="text-[#64748B] truncate">{a.type === "visio_1h" ? "Visio 1h" : a.type === "phone_15m" ? "Tél 15 min" : "Physique"}</div>
                      </div>
                    ))}
                    {/* Google Calendar events */}
                    {dayGoogle.map((e) => {
                      const t = (e.start || "").includes("T") ? new Date(e.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "Toute la journée";
                      return (
                        <div key={e.id} className="bg-[#A7F3D0]/25 border-l-2 border-[#A7F3D0] px-2 py-1 rounded text-[11px]" title="Événement Google Calendar" data-testid={`agenda-week-gcal-${e.id}`}>
                          <div className="font-bold tabular-nums text-[#065F46] flex items-center gap-1"><CalIcon size={9} />{t}</div>
                          <div className="text-[#0A1128] truncate font-medium">{e.summary || "(sans titre)"}</div>
                          <div className="text-[#64748B] text-[9px] uppercase tracking-wider">Google</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === "month" && (
        <div className="mt-4">
          <div className="grid grid-cols-7 gap-1 text-[10px] font-bold tracking-widest uppercase text-[#64748B] mb-1">
            {WEEKDAYS_SHORT.map((d) => <div key={d} className="text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {loading && <div className="col-span-7 text-center text-sm text-[#64748B] py-10">Chargement…</div>}
            {!loading && monthGrid.map((cell, idx) => {
              if (!cell) return <div key={`pad-${idx}`} />;
              const isSelected = selectedDate === cell.date;
              const isToday = cell.date === todayIso;
              const apptCount = apptCountsByDate[cell.date] || 0;
              const gcalCount = googleEvents.filter((e) => e.dateOnly === cell.date && !e.source_besson_appt_id).length;
              const isBlocked = (blockedByDate[cell.date] || []).length > 0;
              const rate = cell.rate_pct;
              const rateColor = cell.capacity === 0 ? "#cbd5e1" : rate >= 80 ? "#ef4444" : rate >= 50 ? "#06B6D4" : "#A7F3D0";
              return (
                <button
                  key={cell.date}
                  data-testid={`agenda-day-${cell.date}`}
                  onClick={() => setSelectedDate(cell.date)}
                  className={`group relative aspect-square rounded-xl border text-left p-1.5 sm:p-2 transition-all overflow-hidden ${isSelected ? "border-[#06B6D4] ring-2 ring-[#06B6D4]/40 bg-[#06B6D4]/5" : "border-black/8 hover:border-[#06B6D4]/40"} ${isBlocked ? "bg-red-50" : ""}`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`text-xs font-bold ${isToday ? "text-[#06B6D4]" : "text-[#0A1128]"}`}>{Number(cell.date.slice(8, 10))}</span>
                    {isBlocked && <Lock size={10} className="text-red-500" />}
                  </div>
                  {cell.capacity > 0 && (
                    <div className="absolute bottom-1 left-1 right-1">
                      <div className="h-1 rounded-full bg-black/8 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, rate)}%`, background: rateColor }} />
                      </div>
                    </div>
                  )}
                  {apptCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center min-w-[16px] h-4 rounded-full bg-[#0A1128] text-white text-[9px] font-bold px-1">{apptCount}</span>
                  )}
                  {gcalCount > 0 && (
                    <span className="absolute top-7 right-1.5 inline-flex items-center justify-center min-w-[16px] h-4 rounded-full bg-[#A7F3D0] text-[#065F46] text-[9px] font-bold px-1" title={`${gcalCount} événement(s) Google`}>G{gcalCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected day panel (under either view) */}
      {selectedDate && (
        <div className="mt-4 bg-[#FAFAFA] rounded-2xl p-4 border border-black/8" data-testid="agenda-selected-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-[#06B6D4]">Jour sélectionné</div>
              <div className="display font-extrabold text-[#0A1128]">{new Date(selectedDate).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
            </div>
          </div>
          {apptsForSelected.length === 0 ? (
            <div className="mt-3 text-sm text-[#64748B]">Aucun rendez-vous ce jour.</div>
          ) : (
            <ul className="mt-3 space-y-1.5">
              {apptsForSelected.map((a) => (
                <li key={a.id} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-black/5 text-sm">
                  <span className="font-bold text-[#06B6D4] tabular-nums">{a.preferred_time}</span>
                  <span className="font-medium text-[#0A1128]">{a.name}</span>
                  <span className="text-xs text-[#64748B]">· {a.type === "visio_1h" ? "Visio 1h" : a.type === "phone_15m" ? "Tél 15min" : "Physique"}</span>
                  <span className="ml-auto text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E]">{a.status}</span>
                </li>
              ))}
            </ul>
          )}
          {googleEvents.filter((e) => e.dateOnly === selectedDate && !e.source_besson_appt_id).length > 0 && (
            <div className="mt-3" data-testid="agenda-google-events">
              <div className="text-[10px] font-bold tracking-widest uppercase text-[#065F46] mb-1.5">Événements Google Calendar</div>
              <ul className="space-y-1.5">
                {googleEvents.filter((e) => e.dateOnly === selectedDate && !e.source_besson_appt_id).map((e) => {
                  const time = (e.start || "").includes("T") ? new Date(e.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "Toute la journée";
                  return (
                    <li key={e.id} className="flex items-center gap-2 bg-[#A7F3D0]/20 px-3 py-2 rounded-lg border border-[#A7F3D0]/40 text-sm">
                      <span className="font-bold text-[#065F46] tabular-nums">{time}</span>
                      <span className="font-medium text-[#0A1128] truncate">{e.summary || "(sans titre)"}</span>
                      {e.html_link && <a href={e.html_link} target="_blank" rel="noopener noreferrer" className="ml-auto text-[10px] text-[#06B6D4] hover:underline font-bold">Ouvrir →</a>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Block modal */}
      <Dialog open={!!blockModal} onOpenChange={(v) => !v && setBlockModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bloquer un créneau ou une plage (congés)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date de début</Label>
                <Input type="date" value={blockModal?.date || ""} onChange={(e) => setBlockModal((m) => ({ ...m, date: e.target.value }))} data-testid="agenda-block-date" />
              </div>
              <div>
                <Label>Date de fin (optionnelle)</Label>
                <Input type="date" value={blockModal?.date_to || ""} onChange={(e) => setBlockModal((m) => ({ ...m, date_to: e.target.value }))} placeholder="Pour une plage" data-testid="agenda-block-date-to" />
              </div>
            </div>
            <div className="text-[10px] text-[#64748B] -mt-1">Laisser la date de fin vide pour bloquer un seul jour. Une plage bloque toutes les journées entières entre les deux dates.</div>
            <div className="flex items-center gap-2">
              <input id="block-all-day" type="checkbox" checked={blockForm.all_day} onChange={(e) => setBlockForm({ ...blockForm, all_day: e.target.checked })} data-testid="agenda-block-all-day" />
              <Label htmlFor="block-all-day" className="cursor-pointer">Bloquer toute la journée</Label>
            </div>
            {!blockForm.all_day && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Début</Label><Input type="time" value={blockForm.start_time} onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })} data-testid="agenda-block-start" /></div>
                <div><Label>Fin</Label><Input type="time" value={blockForm.end_time} onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })} data-testid="agenda-block-end" /></div>
              </div>
            )}
            <div><Label>Motif (optionnel)</Label><Input value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} placeholder="Ex : congés, formation, rendez-vous personnel..." data-testid="agenda-block-reason" /></div>
            <div className="text-[11px] text-[#64748B] bg-[#FFDCA8]/30 rounded-lg p-2 flex items-start gap-2">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>Les créneaux bloqués n'apparaîtront plus dans le formulaire public de prise de rendez-vous.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockModal(null)}>Annuler</Button>
            <Button onClick={submitBlock} className="bg-[#0A1128] text-white" data-testid="agenda-block-confirm">Confirmer le blocage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="bg-[#FAFAFA] rounded-xl p-3 border border-black/5">
      <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color }}>{label}</div>
      <div className="display text-2xl font-extrabold text-[#0A1128] mt-0.5">{value}</div>
      <div className="text-[10px] text-[#64748B]">{sub}</div>
    </div>
  );
}
